'use client'

import React from 'react'
import { useTranslation } from 'react-i18next'
import { ButtonLink, type ButtonSize, type ButtonVariant } from '@kiao-voice/ui-kit'
import { ANALYTICS_EVENTS, trackEvent, type AnalyticsLocation } from '../../lib/analytics'
import type { DesktopPlatform, ReleaseStatus } from '../../lib/latest-release'
import { PlatformIcon } from './platform-icon'

interface DownloadButtonProps {
  release: ReleaseStatus
  fallbackUrl?: string
  variant?: ButtonVariant
  /** Shared button geometry for the placement (landing CTA, footer, etc.). */
  size?: ButtonSize
  className?: string
  /** Which desktop build this button downloads — drives the label + analytics. */
  platform?: DesktopPlatform
  /** Placement label so downloads can be compared by position (cta_section vs footer vs …). */
  location?: AnalyticsLocation
  /**
   * Render a muted, non-interactive 「準備中」 placeholder instead of a download
   * link — used for a build that isn't ready to ship yet (today: Windows while
   * it stabilises). Gated server-side by the `desktop.windows_download_enabled`
   * flag; no href and no analytics are emitted in this state.
   */
  comingSoon?: boolean
}

/**
 * 下載 CTA（黑白 mono 版）：實心墨黑 / 描邊兩種，樣式走 ui-kit ButtonLink。
 * href 資料流不變：release manifest URL → R2 fallback URL。
 * `platform` 切換文案（macOS / Windows）並標記 analytics。
 * macOS 按鈕指向 universal DMG，同時支援 Apple Silicon 與 Intel。
 */
export function DownloadButton({
  release,
  fallbackUrl = '',
  variant = 'primary',
  size = 'md',
  className,
  platform = 'macos',
  location,
  comingSoon = false,
}: DownloadButtonProps) {
  const { t } = useTranslation(['common'])
  const icon = <PlatformIcon platform={platform} size={size === 'compact' ? 14 : 16} />
  // Build not ready yet (today: Windows while it stabilises) → a muted,
  // non-interactive placeholder. No href, no analytics. The only coming-soon
  // build is Windows, so the label is the Windows-specific 「準備中」 copy.
  if (comingSoon) {
    return (
      <ButtonLink
        as="span"
        variant={variant}
        size={size}
        className={`${className ?? ''} text-muted !opacity-100`.trim()}
        unavailable
        data-download-state="coming-soon"
      >
        {icon}
        {t('common:windowsComingSoon')}
      </ButtonLink>
    )
  }

  if (release && 'blocked' in release) {
    return (
      <ButtonLink
        as="span"
        variant={variant}
        size={size}
        className={className}
        unavailable
        data-download-state="blocked"
      >
        {icon}
        {t('common:downloadUnavailable')}
      </ButtonLink>
    )
  }

  const href = release?.downloadUrl || fallbackUrl || undefined
  const label = t(platform === 'windows' ? 'common:platformWindows' : 'common:platformMacOS')
  const ariaLabel = t(
    platform === 'windows' ? 'common:downloadWindowsAriaLabel' : 'common:downloadMacOSAriaLabel'
  )

  if (!href) {
    return (
      <ButtonLink as="span" variant={variant} size={size} className={className} unavailable>
        {icon}
        {t('common:downloadUnavailable')}
      </ButtonLink>
    )
  }

  return (
    <ButtonLink
      href={href}
      aria-label={ariaLabel}
      variant={variant}
      size={size}
      className={className}
      onClick={() => {
        trackEvent(ANALYTICS_EVENTS.downloadAppClick, {
          platform,
          ...(location ? { location } : {}),
        })
      }}
    >
      {icon}
      {label}
    </ButtonLink>
  )
}
